import { injectable } from 'inversify';
import mergeWith from 'lodash/mergeWith';
import { createBaseMapper } from './base-mapper';
import type { BaseDocumentModel } from './models';
import type { DocumentId, DocumentRevision } from '../types';
import type { BaseSseRepository } from './BaseSse.repository';
import { BaseDocumentChangeModel, DocumentOperations } from './models';
import type { ChangesResource } from './interfaces';
import type { BaseCRUDRepository } from './BaseCRUD.repository';
import type { BaseDocumentVM } from './view-models';

@injectable()
export class BaseChangesService<
  TEntity extends BaseDocumentVM = BaseDocumentVM,
  TModel extends BaseDocumentModel = BaseDocumentModel,
> implements ChangesResource<TEntity, TModel>
{
  protected mapper = createBaseMapper<TModel, TEntity>();

  protected documentsRepo: BaseCRUDRepository<TModel>;

  protected sseRepo: BaseSseRepository;

  // eslint-disable-next-line @typescript-eslint/require-await
  async createSSE(id: DocumentId) {
    if (!this.sseRepo) return null;

    return this.sseRepo.create(id);
  }

  async getMergedChanges(id: DocumentId, since: DocumentRevision) {
    const changes = await this.documentsRepo.getChanges(id, since);

    return changes.reduce((acc, change) => {
      if (change.revision === since || change.operation === DocumentOperations.create) return acc;

      return BaseChangesService.mergeChanges(acc, change);
    }, new BaseDocumentChangeModel<TModel>());
  }

  async getAndApplyChanges(entity: TEntity) {
    const changes = await this.getMergedChanges(entity._id, entity._rev);

    return this.applyChanges([entity], [changes]);
  }

  /**
   * Applying changes is possible just on api level because of different models.
   */
  // eslint-disable-next-line @typescript-eslint/require-await
  applyChanges(entities: TEntity[] | null, models: BaseDocumentChangeModel[]) {
    if (!entities) {
      return this.mapper.toDomainArray(models.map(p => (p.patch ?? p.change) as TModel));
    }

    const applyPatch = (entity: TEntity, patch: BaseDocumentChangeModel) => {
      const currentRev = entity._rev;
      const patchRev = patch.revision;

      if (currentRev >= patchRev) {
        return entity;
      }

      // Convert entity to model to be able apply patch
      const model = this.mapper.fromDomain(entity);

      const mergedModel = BaseChangesService.mergeChanges(model, {
        ...(patch.patch ?? patch.change),
        _rev: patch.revision,
      });

      // Return updated entity
      return this.mapper.toDomain(mergedModel);
    };

    return entities.map((entity, i) => applyPatch(entity, models[i]));
  }

  static mergeChanges<TModel, TPatch>(model: TModel, patch: TPatch) {
    return mergeWith({}, model, patch, (_model: unknown, _patch: unknown) => {
      if (Array.isArray(_model)) {
        return _patch; // if array was changed - override old one
      }
    });
  }
}
